// src/design/DesignCenterWorkspace.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import DesignCenterAIAssist from './DesignCenterAIAssist';
import PublishPreviewModal from './PublishPreviewModal';
import PexelsSearchModal from './PexelsSearchModal';
import LandingPageBuilder from './LandingPageBuilder';
import ModerationWarning from '../components/common/ModerationWarning';
import { useModalStore } from '../state/modalStore';
import { useProfileStore } from '../state/profileStore';
import { getStrings } from './designCenterLocale';

const API_URL = process.env.REACT_APP_API_URL;
const PAGE_SIZE = 40;

// ─── Color palette for headline overlay ──────────────────────────────────────
const HEADLINE_COLORS = [
  { name: 'White',      hex: '#FFFFFF' },
  { name: 'Black',      hex: '#000000' },
  { name: 'Yellow',     hex: '#FFD700' },
  { name: 'Red',        hex: '#E53E3E' },
  { name: 'Orange',     hex: '#F97316' },
  { name: 'Hot Pink',   hex: '#EC4899' },
  { name: 'Lime',       hex: '#84CC16' },
  { name: 'Cyan',       hex: '#06B6D4' },
  { name: 'Royal Blue', hex: '#2563EB' },
  { name: 'Purple',     hex: '#8B5CF6' },
  { name: 'Coral',      hex: '#F87171' },
  { name: 'Slate',      hex: '#475569' },
];

// ─── Draggable + Resizable Headline Overlay ─────────────────────────────────
// Reusable overlay that works on both images and videos.
// Takes a containerRef to calculate drag/resize positions relative to parent.
function HeadlineOverlay({ containerRef, headline, onHeadlineChange, headlinePos, onHeadlinePosChange, headlineStyle, onHeadlineStyleChange }) {
  const [editingHeadline, setEditingHeadline] = useState(false);
  const [editText, setEditText] = useState('');
  const editRef = useRef(null);
  const headlineDragState = useRef(null);
  const resizeDragState = useRef(null);

  const onHeadlinePointerDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const container = containerRef.current;
    if (!container) return;
    const cRect = container.getBoundingClientRect();
    headlineDragState.current = {
      startX: e.clientX, startY: e.clientY,
      startPosX: headlinePos?.x ?? 50, startPosY: headlinePos?.y ?? 75,
      cW: cRect.width, cH: cRect.height,
    };
  }, [containerRef, headlinePos]);

  const onResizePointerDown = useCallback((e, side) => {
    e.preventDefault();
    e.stopPropagation();
    const container = containerRef.current;
    if (!container) return;
    const cRect = container.getBoundingClientRect();
    resizeDragState.current = {
      side, startX: e.clientX,
      startWidth: headlineStyle?.boxWidth ?? 60,
      cW: cRect.width,
    };
  }, [containerRef, headlineStyle?.boxWidth]);

  useEffect(() => {
    const onMove = (e) => {
      const rs = resizeDragState.current;
      if (rs) {
        const dx = e.clientX - rs.startX;
        const dxPct = (dx / rs.cW) * 100;
        const widthDelta = rs.side === 'right' ? dxPct * 2 : -dxPct * 2;
        const newWidth = Math.max(15, Math.min(95, rs.startWidth + widthDelta));
        if (onHeadlineStyleChange) onHeadlineStyleChange((s) => ({ ...s, boxWidth: newWidth }));
        return;
      }
      const ds = headlineDragState.current;
      if (!ds) return;
      const dx = e.clientX - ds.startX;
      const dy = e.clientY - ds.startY;
      const newX = Math.max(5, Math.min(95, ds.startPosX + (dx / ds.cW) * 100));
      const newY = Math.max(5, Math.min(75, ds.startPosY + (dy / ds.cH) * 100)); // max 75% — keeps caption above platform UI overlays (TikTok, YouTube, Facebook, Instagram)
      if (onHeadlinePosChange) onHeadlinePosChange({ x: newX, y: newY });
    };
    const onUp = () => { headlineDragState.current = null; resizeDragState.current = null; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
  }, [onHeadlinePosChange, onHeadlineStyleChange]);

  if (!headline) return null;

  const fontSize = headlineStyle?.fontSize ?? 14;
  const color = headlineStyle?.color ?? '#FFFFFF';
  const bgOpacity = headlineStyle?.bgOpacity ?? 0.6;
  const boxWidth = headlineStyle?.boxWidth ?? 60;
  const isLightColor = ['#FFFFFF', '#FFD700', '#84CC16', '#F87171', '#F97316'].includes(color);
  const bgColor = isLightColor ? `rgba(0,0,0,${bgOpacity})` : `rgba(255,255,255,${bgOpacity})`;
  const textShadow = isLightColor ? '0 1px 4px rgba(0,0,0,0.7)' : '0 1px 4px rgba(255,255,255,0.5)';

  return (
    <div
      onPointerDown={onHeadlinePointerDown}
      className="absolute pointer-events-auto"
      style={{
        left: `${headlinePos?.x ?? 50}%`, top: `${headlinePos?.y ?? 75}%`,
        transform: 'translate(-50%, -50%)', width: `${boxWidth}%`,
        cursor: 'move', touchAction: 'none', userSelect: 'none',
      }}
    >
      <div
        onPointerDown={(e) => onResizePointerDown(e, 'left')}
        className="absolute top-0 bottom-0 -left-1 w-2.5 flex items-center justify-center"
        style={{ cursor: 'ew-resize', zIndex: 2 }}
      >
        <div className="w-0.5 h-5 bg-white/70 rounded-full" />
      </div>
      <div
        className="px-3 py-1.5 rounded"
        style={{ background: bgOpacity > 0 ? bgColor : 'transparent', backdropFilter: bgOpacity > 0 ? 'blur(4px)' : 'none' }}
        onDoubleClick={(e) => {
          e.preventDefault(); e.stopPropagation();
          setEditText(headline); setEditingHeadline(true);
          setTimeout(() => {
            const ta = editRef.current;
            if (ta) { ta.focus(); ta.style.height = 'auto'; ta.style.height = ta.scrollHeight + 'px'; }
          }, 0);
        }}
      >
        {editingHeadline ? (
          <textarea
            ref={editRef}
            value={editText}
            onChange={(e) => {
              setEditText(e.target.value);
              const ta = e.target;
              ta.style.height = 'auto';
              ta.style.height = ta.scrollHeight + 'px';
            }}
            onBlur={() => { const t = editText.trim(); if (t && onHeadlineChange) onHeadlineChange(t); setEditingHeadline(false); }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditingHeadline(false);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className="font-bold leading-tight block text-center w-full bg-transparent border-none outline-none resize-none p-0 m-0"
            style={{ fontSize: `${fontSize}px`, color, textShadow, wordWrap: 'break-word', overflowWrap: 'break-word', caretColor: color, minHeight: '1.2em', overflow: 'hidden' }}
          />
        ) : (
          <span
            className="font-bold leading-tight block text-center"
            style={{ fontSize: `${fontSize}px`, color, textShadow, wordWrap: 'break-word', overflowWrap: 'break-word', cursor: 'move' }}
            title="Double-click to edit text"
          >
            {headline}
          </span>
        )}
      </div>
      <div
        onPointerDown={(e) => onResizePointerDown(e, 'right')}
        className="absolute top-0 bottom-0 -right-1 w-2.5 flex items-center justify-center"
        style={{ cursor: 'ew-resize', zIndex: 2 }}
      >
        <div className="w-0.5 h-5 bg-white/70 rounded-full" />
      </div>
    </div>
  );
}

// ─── Draggable Image Preview ───────────────────────────────────────────────────
// Renders the image inside a fixed-aspect container with object-cover.
// User can click-drag to reposition the image within the crop window.
// Stores position as { x: 0-100, y: 0-100 } percentages (object-position).
// Zero AI cost — pure CSS/JS.
function DraggablePreview({ src, alt, position, zoom, onPositionChange, onZoomChange, headline, onHeadlineChange, headlinePos, onHeadlinePosChange, headlineStyle, onHeadlineStyleChange }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const dragState = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Mouse down — start drag
  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img) return;

    const cRect = container.getBoundingClientRect();
    const naturalW = img.naturalWidth || 1;
    const naturalH = img.naturalHeight || 1;

    // object-cover scale × user zoom
    const scaleX = cRect.width / naturalW;
    const scaleY = cRect.height / naturalH;
    const baseScale = Math.max(scaleX, scaleY);
    const scale = baseScale * zoom;

    const renderedW = naturalW * scale;
    const renderedH = naturalH * scale;

    const overflowX = renderedW - cRect.width;
    const overflowY = renderedH - cRect.height;

    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: position.x,
      startPosY: position.y,
      overflowX,
      overflowY,
    };

    container.style.cursor = 'grabbing';
  }, [position, zoom]);

  // Mouse move / up
  useEffect(() => {
    const onPointerMove = (e) => {
      const ds = dragState.current;
      if (!ds) return;

      const deltaX = e.clientX - ds.startX;
      const deltaY = e.clientY - ds.startY;

      let newX = ds.startPosX;
      let newY = ds.startPosY;

      if (ds.overflowX > 1) {
        const pctShift = (deltaX / ds.overflowX) * -100;
        newX = Math.max(0, Math.min(100, ds.startPosX + pctShift));
      }

      if (ds.overflowY > 1) {
        const pctShift = (deltaY / ds.overflowY) * -100;
        newY = Math.max(0, Math.min(100, ds.startPosY + pctShift));
      }

      onPositionChange({ x: newX, y: newY });
    };

    const onPointerUp = () => {
      if (dragState.current && containerRef.current) {
        containerRef.current.style.cursor = 'grab';
      }
      dragState.current = null;
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [onPositionChange]);

  // Scroll wheel — zoom in/out
  // Must use a native non-passive listener so preventDefault() actually works
  // and stops the event from scrolling parent containers at min/max zoom.
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const onZoomChangeRef = useRef(onZoomChange);
  onZoomChangeRef.current = onZoomChange;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Math.max(1, Math.min(5, zoomRef.current + delta));
      onZoomChangeRef.current(newZoom);
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);


  return (
    <div
      ref={containerRef}
      onPointerDown={onPointerDown}
      className="relative w-full aspect-[4/3] rounded-lg overflow-hidden border border-gray-200 bg-gray-100 select-none"
      style={{ cursor: 'grab', touchAction: 'none' }}
    >
      {/* Loading spinner — visible until image loads */}
      {!imgLoaded && (
        <div className="absolute inset-0 flex items-center justify-center z-[1]">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-[#00aef7]" />
        </div>
      )}
      <img
        ref={imgRef}
        src={src}
        alt=""
        draggable={false}
        className="pointer-events-none"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: `${position.x}% ${position.y}%`,
          transform: `scale(${zoom})`,
          transformOrigin: `${position.x}% ${position.y}%`,
          opacity: imgLoaded ? 1 : 0,
        }}
        onLoad={(e) => {
          setImgLoaded(true);
          e.currentTarget.style.display = '';
        }}
        onError={(e) => {
          setImgLoaded(true); // clear spinner even on error
          e.currentTarget.style.display = 'none';
        }}
      />

      <HeadlineOverlay
        containerRef={containerRef}
        headline={headline}
        onHeadlineChange={onHeadlineChange}
        headlinePos={headlinePos}
        onHeadlinePosChange={onHeadlinePosChange}
        headlineStyle={headlineStyle}
        onHeadlineStyleChange={onHeadlineStyleChange}
      />
    </div>
  );
}

// ─── Main Workspace ────────────────────────────────────────────────────────────
export default function DesignCenterWorkspace({
  activeTabLabel,
  subcategoryKey,
  onHasActiveWork,
}) {
  const openSharingCenter = useModalStore((s) => s.openSharingCenter);
  const setSharingCenterPending = useModalStore((s) => s.setSharingCenterPending);
  const setSharingCenterModerationMsg = useModalStore((s) => s.setSharingCenterModerationMsg);
  const bringToFront = useModalStore((s) => s.bringToFront);

  // For inline copy that needs the user's industry — same fallback chain
  // designCenter.jsx uses for its header.
  const profile = useProfileStore((s) => s.profile);

  // Localized strings for the inspiration block (and any future Workspace-
  // owned UI). Reads u_preferred_language from the already-hydrated
  // profileStore — no separate fetch. Falls back to English when profile
  // hasn't loaded yet or the user hasn't set a preference. Same translation
  // pool the AI assistant uses, just consumed at render-time here.
  const userLang = profile?.u_preferred_language || 'en';
  const inspStrings = useMemo(() => getStrings(userLang).inspiration, [userLang]);

  // Image data — accumulated for infinite scroll
  const [images, setImages] = useState([]);
  const [professionName, setProfessionName] = useState('');
  const [professionId, setProfessionId] = useState(null);
  const [businessInfo, setBusinessInfo] = useState({ name: '', city: '', state: '' });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [moderationAlert, setModerationAlert] = useState(null);

  // Selected image
  const [selectedFilename, setSelectedFilename] = useState(null);

  // Per-image crop positions and zoom: { [filename]: { x, y } }
  // Persists as long as component is mounted so user doesn't lose work switching images
  const [cropPositions, setCropPositions] = useState({});
  const [zoomLevels, setZoomLevels] = useState({});

  // AI-generated headline + captions (in memory only)
  const [headline, setHeadline] = useState('');
  // Tracks WHO/WHAT set the current headline. Used to decide whether to
  // pass it to the AI generator as a 'locked, do not translate/rephrase'
  // value. Provenance:
  //   'user'        — user typed it in the headline overlay (lock + preserve verbatim)
  //   'inspiration' — auto-set from clicking an inspiration card (DON'T lock — let
  //                   the AI generate a fresh headline in the user's preferred
  //                   language; the card's title was a topic, not a final wording)
  //   'ai'          — AI generated it (already in the right language; no relock needed)
  //   null          — unset / cleared
  // Bug surfaced 2026-05-25: Lekkey (Thai preference) picked an English
  // inspiration card, headline went to backend as 'locked English', AI
  // returned it verbatim → English headline on a Thai post.
  const [headlineSource, setHeadlineSource] = useState(null);
  // Wrapper for the HeadlineOverlay's onChange — flags the headline as
  // user-typed so it gets LOCKED on the next AI call (preserved verbatim,
  // no translation). Use this for ANY UI surface where the user directly
  // edits the headline; use raw setHeadline only for inspiration/AI sources.
  const setHeadlineFromUser = useCallback((text) => {
    setHeadline(text);
    setHeadlineSource('user');
  }, []);
  const [captions, setCaptions] = useState(null);
  const [platformOverlays, setPlatformOverlays] = useState(null);
  // Headline overlay position on the image (percentage-based, draggable)
  const [headlinePos, setHeadlinePos] = useState({ x: 50, y: 25 }); // default: near top (avoids platform UI overlays at bottom)
  // Headline styling: font size (px), color (hex), background opacity (0-1), boxWidth (% of container)
  const [headlineStyle, setHeadlineStyle] = useState({ fontSize: 14, color: '#FFFFFF', bgOpacity: 0.6, boxWidth: 60 });

  // Publish preview modal
  const [showPreview, setShowPreview] = useState(false);
  const [previewKey, setPreviewKey] = useState(0); // bumped each open to force fresh mount

  // Landing page decision + builder
  const [showLandingDecision, setShowLandingDecision] = useState(false);
  const [tiktokVideoAssetId, setTiktokVideoAssetId] = useState(null);
  const tiktokVideoAssetIdRef = useRef(null);
  // Track which source image the current Ken Burns video was generated from.
  // When the user swaps to a different image, the old video is stale — clear it.
  const kenBurnsSourceAssetIdRef = useRef(null);
  const [externalUrl, setExternalUrl] = useState('');
  const [showLandingBuilder, setShowLandingBuilder] = useState(false);
  const [landingPageId, setLandingPageId] = useState(null);
  const [landingPageData, setLandingPageData] = useState(null);
  const [generatingLanding, setGeneratingLanding] = useState(false);

  // Pexels search modal
  const [showPexels, setShowPexels] = useState(false);
  const [pexelsInitialQuery, setPexelsInitialQuery] = useState('');
  const [pexelsSearchMode, setPexelsSearchMode] = useState(null); // 'video' or null (image default)

  // AI brief (bubbled up from DesignCenterAIAssist when user finishes all questions)
  const [aiBrief, setAiBrief] = useState(null);
  const [aiResetKey, setAiResetKey] = useState(0);

  // Inspiration board
  const [inspirationIdeas, setInspirationIdeas] = useState(null);
  const [inspirationLoading, setInspirationLoading] = useState(false);
  const [inspirationDismissed, setInspirationDismissed] = useState(false);
  const [inspirationAnswer, setInspirationAnswer] = useState('');
  // Crowd-sourced "generate new" — prepends to the cohort-shared pool
  const [generatingNewInspiration, setGeneratingNewInspiration] = useState(false);
  // Optional seed-term modal. User clicks Generate New → modal opens with a
  // single input. Empty submission → random fresh idea (current behavior).
  // Non-empty → AI generates around that term (e.g. "x-ray machine", "nano
  // microscope"), so a multi-product business like Thom's can steer the idea
  // without having to describe the whole operation each time.
  const [generateNewPromptOpen, setGenerateNewPromptOpen] = useState(false);
  const [generateNewPromptText, setGenerateNewPromptText] = useState('');
  // Profession-specific placeholder examples for the seed-term input. We sample
  // 4 random `pexels_search_term` values from the currently-loaded inspiration
  // ideas — they're already concrete nouns specific to this profession (the
  // server prompt forces "concrete nouns only" for that field), so they double
  // as great placeholder hints. Recomputes only when the idea pool changes
  // (locked by useMemo dep) so the placeholder doesn't shuffle on every render.
  const generateNewPlaceholder = useMemo(() => {
    if (!Array.isArray(inspirationIdeas) || inspirationIdeas.length === 0) {
      return 'e.g. any specific topic you want to focus on';
    }
    const terms = inspirationIdeas
      .map((idea) => (idea?.pexels_search_term || '').trim())
      .filter(Boolean);
    const unique = Array.from(new Set(terms));
    // Fisher-Yates shuffle
    for (let i = unique.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [unique[i], unique[j]] = [unique[j], unique[i]];
    }
    const sample = unique.slice(0, 4);
    return sample.length > 0 ? `e.g. ${sample.join(', ')}` : 'e.g. any specific topic you want to focus on';
  }, [inspirationIdeas]);
  // Ref on the horizontal scroll strip so we can snap back to the start after
  // a new idea is generated (user clicked the button at left; new card just
  // appeared next to it — but the user may have been scrolled far right when
  // they clicked).
  const inspirationStripRef = useRef(null);

  // User upload
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Video
  const [videoExpanded, setVideoExpanded] = useState(false);
  const inlineVideoRef = useRef(null);
  const expandedVideoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const videoTimeRef = useRef(0);
  const videoWasPlayingRef = useRef(false);

  // Refs
  const scrollRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const pagesLoadedRef = useRef(0);

  // Clear post-specific design state after successful publish.
  // Keeps the image gallery, selected image, and inspiration intact so the
  // workspace looks ready for the next design — not wiped blank.
  const resetWorkspace = useCallback(() => {
    setHeadline('');
    setHeadlineSource(null);
    setCaptions(null);
    setPlatformOverlays(null);
    setHeadlinePos({ x: 50, y: 25 });
    setHeadlineStyle({ fontSize: 14, color: '#FFFFFF', bgOpacity: 0.6, boxWidth: 60 });
    setExternalUrl('');
    setShowLandingDecision(false);
    setShowLandingBuilder(false);
    setLandingPageId(null);
    setLandingPageData(null);
    setGeneratingLanding(false);
    setAiBrief(null);
    setModerationAlert(null);
    setShowPreview(false);
    setInspirationAnswer('');
    setInspirationDismissed(true);
    setAiResetKey((k) => k + 1);
  }, []);

  // Reset AI/inspiration state when subcategory changes.
  // Images stay — all categories share the same profession images,
  // so clearing + re-fetching the same set causes unnecessary flicker
  // and a race condition where the first image fails to display.
  const prevSubcatRef = useRef(subcategoryKey);
  useEffect(() => {
    setVideoExpanded(false);
    setInspirationAnswer('');

    // Always clear any previously generated TikTok/YouTube FFmpeg video when
    // the subcategory changes — that video was rendered from the OLD image
    // selection and no longer matches the new category's content.
    const isChange = prevSubcatRef.current !== subcategoryKey;
    if (isChange) {
      setTiktokVideoAssetId(null);
      tiktokVideoAssetIdRef.current = null;
    }

    // Only re-fetch images on initial mount or if the images haven't loaded yet
    if (isChange && images.length > 0) {
      // Same profession images — skip refetch, just reset AI state
      prevSubcatRef.current = subcategoryKey;
      return;
    }
    prevSubcatRef.current = subcategoryKey;

    // First load or empty — do the full reset + fetch
    setSelectedFilename(null);
    setImages([]);
    setTotal(0);
    setHasMore(true);
    setError(null);
    setCropPositions({});
    setZoomLevels({});
    loadingMoreRef.current = false;
    pagesLoadedRef.current = 0;
  }, [subcategoryKey]);

  // Fetch a page of images
  const fetchPage = useCallback(async (subcat, pg, append) => {
    if (!subcat) return;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const url = `${API_URL}/api/designcenter/profession-images?subcategory=${encodeURIComponent(subcat)}&page=${pg}&limit=${PAGE_SIZE}`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        setError(data.error || 'Failed to load images');
        if (!append) { setImages([]); setTotal(0); }
        return;
      }

      const newImages = data.images || [];
      setProfessionName(data.profession_name || '');
      setProfessionId(data.profession_id || null);
      console.log('>>> [DesignCenter] business info from API:', { business_name: data.business_name, business_city: data.business_city, business_state: data.business_state });
      setBusinessInfo({
        name: data.business_name || '',
        city: data.business_city || '',
        state: data.business_state || '',
      });
      setTotal(data.total || 0);
      pagesLoadedRef.current = pg;

      if (append) {
        setImages((prev) => [...prev, ...newImages]);
      } else {
        setImages(newImages);
        if (newImages.length) {
          // Preserve the caller's selection if it still appears in the refreshed
          // list (e.g., a just-imported Pexels video whose row may not sort to
          // position 0). Otherwise leave selection as null — Brett's call
          // 2026-06-01: entering Design Center to start a new design should NOT
          // auto-load the previous creation's image/video. The canvas shows the
          // "Choose or upload a video or image" empty state until the user
          // explicitly clicks a gallery thumbnail, uploads, or imports.
          setSelectedFilename((current) =>
            current && newImages.some((img) => img.filename === current)
              ? current
              : null
          );
        }
      }

      setHasMore(pg * PAGE_SIZE < (data.total || 0));
    } catch (err) {
      console.error('[DesignCenterWorkspace] fetch error:', err);
      setError('Network error loading images');
      if (!append) setImages([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, []);

  // Initial load — only fetch if images are empty (category switches skip this)
  useEffect(() => {
    if (subcategoryKey && !images.length) {
      fetchPage(subcategoryKey, 1, false);
    }
  }, [subcategoryKey, images.length, fetchPage]);

  // Fetch inspiration ideas (lazy — only on first visit per profession/section)
  // Starts dismissed — user opens via lightbulb on the image
  useEffect(() => {
    if (!subcategoryKey) return;
    setInspirationDismissed(true);
    setInspirationLoading(true);
    setInspirationIdeas(null);
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/designcenter/inspiration?section=${encodeURIComponent(subcategoryKey)}`, { credentials: 'include' });
        const data = await res.json();
        if (!cancelled && data.ok && Array.isArray(data.ideas) && data.ideas.length > 0) {
          setInspirationIdeas(data.ideas);
        }
      } catch (err) {
        console.error('[DesignCenter] inspiration fetch error:', err);
      } finally {
        if (!cancelled) setInspirationLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [subcategoryKey]);

  // Intercept native fullscreen on inline video → open our expanded overlay instead
  useEffect(() => {
    const vid = inlineVideoRef.current;
    if (!vid) return;

    const handleFullscreen = (e) => {
      // If entering fullscreen, exit it and open our overlay
      if (document.fullscreenElement) {
        document.exitFullscreen();
        videoTimeRef.current = vid.currentTime;
        videoWasPlayingRef.current = !vid.paused;
        vid.pause();
        setVideoExpanded(true);
      }
    };

    vid.addEventListener('fullscreenchange', handleFullscreen);
    vid.addEventListener('webkitfullscreenchange', handleFullscreen);
    return () => {
      vid.removeEventListener('fullscreenchange', handleFullscreen);
      vid.removeEventListener('webkitfullscreenchange', handleFullscreen);
    };
  });

  // Infinite scroll — load more when within 300px of bottom
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadingMoreRef.current || !hasMore) return;

    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 300;
    if (nearBottom) {
      const nextP = pagesLoadedRef.current + 1;
      loadingMoreRef.current = true;
      fetchPage(subcategoryKey, nextP, true);
    }
  }, [hasMore, subcategoryKey, fetchPage]);

  // Selected image object
  const selected = useMemo(() => {
    if (!selectedFilename || !images.length) return null;
    return images.find((img) => img.filename === selectedFilename) || images[0] || null;
  }, [images, selectedFilename]);

  // Invalidate the generated Ken Burns video whenever the source image changes.
  // Prevents the modal from showing a stale video that was rendered from a
  // different image.
  useEffect(() => {
    if (!selected?.asset_id) return;
    const prev = kenBurnsSourceAssetIdRef.current;
    if (prev && prev !== selected.asset_id) {
      setTiktokVideoAssetId(null);
      tiktokVideoAssetIdRef.current = null;
    }
    kenBurnsSourceAssetIdRef.current = selected.asset_id;
  }, [selected?.asset_id]);

  // Get/set crop position + zoom for the selected image
  const selectedPosition = selectedFilename
    ? (cropPositions[selectedFilename] || { x: 50, y: 50 })
    : { x: 50, y: 50 };

  const selectedZoom = selectedFilename
    ? (zoomLevels[selectedFilename] || 1)
    : 1;

  const handleHideAsset = useCallback(async (assetId) => {
    try {
      const res = await fetch(`${API_URL}/api/designcenter/assets/hide`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_id: assetId }),
      });
      const data = await res.json();
      if (data.ok) {
        // Update local state — toggle hidden flag and re-sort (hidden to bottom)
        setImages((prev) => {
          const updated = prev.map((img) =>
            img.asset_id === assetId ? { ...img, hidden: data.hidden } : img
          );
          // Sort: non-hidden first, then hidden
          return updated.sort((a, b) => (a.hidden === b.hidden ? 0 : a.hidden ? 1 : -1));
        });
      }
    } catch (err) {
      console.error('[DesignCenter] hide asset error:', err);
    }
  }, []);

  const handlePositionChange = useCallback((pos) => {
    if (!selectedFilename) return;
    setCropPositions((prev) => ({ ...prev, [selectedFilename]: pos }));
  }, [selectedFilename]);

  const handleZoomChange = useCallback((z) => {
    if (!selectedFilename) return;
    setZoomLevels((prev) => ({ ...prev, [selectedFilename]: z }));
  }, [selectedFilename]);

  // Reset position + zoom for current image
  const handleResetPosition = useCallback(() => {
    if (!selectedFilename) return;
    setCropPositions((prev) => ({ ...prev, [selectedFilename]: { x: 50, y: 50 } }));
    setZoomLevels((prev) => ({ ...prev, [selectedFilename]: 1 }));
  }, [selectedFilename]);

  const hasEdits = selectedPosition.x !== 50 || selectedPosition.y !== 50 || selectedZoom !== 1;

  // Called by DesignCenterAIAssist when OpenAI returns headline + captions
  const handleCaptionsGenerated = useCallback((data) => {
    setHeadline(data.headline || '');
    setHeadlineSource('ai');
    setCaptions(data.captions || null);
  }, []);

  // Notify parent when user has active work (headline or captions exist)
  useEffect(() => {
    if (onHasActiveWork) {
      onHasActiveWork(!!(headline || captions));
    }
  }, [headline, captions, onHasActiveWork]);

  // User file upload handler
  // Crowd-sourced "Generate New" button at the end of the inspiration strip.
  // Server alternates media type automatically (image ↔ video) based on the
  // last item, and atomically appends to the shared cohort pool so the next
  // teammate sees the expanded list. Natural throttle: user has to scroll
  // past every existing card to reach the button.
  const handleGenerateNewInspiration = useCallback(async (userHint = '') => {
    if (!subcategoryKey || generatingNewInspiration) return;
    setGeneratingNewInspiration(true);
    try {
      const res = await fetch(`${API_URL}/api/designcenter/inspiration/generate-new`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section: subcategoryKey,
          userHint: (userHint || '').trim().slice(0, 120),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok || !data.idea) {
        console.error('[DesignCenter] generate-new inspiration failed:', data.error);
        return;
      }
      // Prepend to local state so the new idea lands right next to the
      // "Generate New" button (also at the start of the strip). Other users
      // of the same profession pick it up next time they load this section.
      setInspirationIdeas((prev) => (Array.isArray(prev) ? [data.idea, ...prev] : [data.idea]));
      // Snap the scroll strip back to the start so the user sees the new
      // card next to the button, even if they had scrolled far right before
      // clicking. Smooth behavior feels like a little confirmation nudge.
      if (inspirationStripRef.current) {
        inspirationStripRef.current.scrollTo({ left: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('[DesignCenter] generate-new inspiration error:', err);
    } finally {
      setGeneratingNewInspiration(false);
    }
  }, [subcategoryKey, generatingNewInspiration]);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !subcategoryKey) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subcategory', subcategoryKey);

      const res = await fetch(`${API_URL}/api/designcenter/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        console.error('[DesignCenter] upload failed:', data.error);
        return;
      }

      // Refresh image grid from page 1 so the new upload appears at top.
      // Do NOT setImages([]) here — emptying images unmounts DesignCenterAIAssist
      // (rendered inside {!selected ? ... : ...}) and wipes the chat history.
      pagesLoadedRef.current = 0;
      setHasMore(true);
      fetchPage(subcategoryKey, 1, false);

      // Auto-select the just-uploaded asset into the editable canvas so the
      // user's intent ("I want to use THIS image/video") lands in one step
      // instead of upload → spot-in-library → click. Works for both images
      // and videos — the /upload endpoint returns the same `filename` field
      // for both, and `selectedFilename` is what drives the canvas preview.
      if (data.filename) {
        setSelectedFilename(data.filename);
      }
    } catch (err) {
      console.error('[DesignCenter] upload error:', err);
    } finally {
      setUploading(false);
      // Reset input so same file can be uploaded again
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [subcategoryKey, fetchPage]);

  return (
    <div className="eh-design-nodrag h-full min-h-0 flex flex-col gap-2">

      {/* ──────── INSPIRATION STRIP ──────── */}
      {/* Visual prominence note: panel is tinted brand-blue (vs the surrounding
          white workspace cards) so it visually separates as "this is a feature,
          not chrome." The lightbulb pulses gently — motion is the strongest
          attention cue available without being garish. */}
      {!inspirationDismissed && (inspirationLoading || (inspirationIdeas && inspirationIdeas.length > 0)) && (
        <div className="shrink-0 bg-gradient-to-r from-[#00aef7]/10 via-[#00aef7]/5 to-transparent rounded-xl border border-[#00aef7]/30 px-3 py-2.5 shadow-sm">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-500 animate-pulse drop-shadow-sm" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.87-3.13-7-7-7zM9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1z" />
              </svg>
              <span className="text-sm font-bold text-gray-800">{inspStrings.need_inspiration}</span>
              <span className="hidden sm:inline-flex items-center gap-1 text-xs text-[#00aef7] font-medium">
                {inspStrings.scroll_for_more(profile?.b_business_industry || professionName || '')}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setInspirationDismissed(true)}
              className="eh-design-nodrag text-gray-400 hover:text-gray-600 p-0.5"
              title={inspStrings.dismiss}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {inspirationLoading ? (
            <div className="flex items-center gap-2 py-3 justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#00aef7]"></div>
              <span className="text-xs text-gray-400">{inspStrings.generating_ideas}</span>
            </div>
          ) : (
            <div
              ref={inspirationStripRef}
              className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
            >
              {/* "Generate New" card — at the START of the strip so the new
                  idea (which prepends to the pool) lands right next to this
                  button. Users see their contribution without scrolling.
                  Click opens a seed-term prompt; leaving it blank = random. */}
              <button
                type="button"
                onClick={() => {
                  setGenerateNewPromptText('');
                  setGenerateNewPromptOpen(true);
                }}
                disabled={generatingNewInspiration}
                className={`eh-design-nodrag shrink-0 w-[200px] rounded-lg border-2 border-dashed ${
                  generatingNewInspiration
                    ? 'border-gray-300 bg-gray-50 cursor-wait'
                    : 'border-[#00aef7]/40 bg-[#00aef7]/5 hover:border-[#00aef7] hover:bg-[#00aef7]/10'
                } p-2.5 flex flex-col items-center justify-center gap-1.5 transition`}
                title={inspStrings.generate_new_title}
              >
                {generatingNewInspiration ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#00aef7]"></div>
                    <span className="text-[10px] font-medium text-gray-500 text-center leading-tight">
                      {inspStrings.generating_new}
                    </span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 text-[#00aef7]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-xs font-semibold text-[#00aef7] text-center leading-tight">
                      {inspStrings.generate_new_btn}
                    </span>
                    <span className="text-[9px] text-gray-400 text-center leading-snug">
                      {inspStrings.generate_new_subtitle}
                    </span>
                  </>
                )}
              </button>

              {inspirationIdeas.map((idea, idx) => (
                <div
                  key={idx}
                  className="shrink-0 w-[200px] rounded-lg border border-gray-200 bg-gray-50 p-2.5 flex flex-col gap-1.5 hover:border-[#00aef7]/40 hover:shadow-sm transition"
                >
                  {/* Media-type badge removed 2026-05-31 per Brett — videos
                      now work on every platform and the image/video split
                      adds friction without value. Freed vertical space
                      reallocated to the headline + caption below. The
                      `media_type` field is still present on inspiration
                      objects (backend hasn't changed); just not rendered. */}
                  <h5 className="text-xs font-semibold text-gray-800 leading-tight line-clamp-3">{idea.title}</h5>
                  <p className="text-[10px] text-gray-400 italic leading-snug line-clamp-3">"{idea.suggested_caption}"</p>
                  <button
                    type="button"
                    onClick={() => {
                      // "Use this idea" no longer auto-opens the Pexels
                      // modal (Brett 2026-05-31). It just imports the
                      // idea content — headline + caption + pexels search
                      // term — and lets the user pick their own path
                      // afterward (Search Images / Search Videos buttons
                      // below, or upload-your-own). SE Asia users churn
                      // out videos daily; forcing them through a video
                      // modal first was unnecessary friction.
                      setPexelsInitialQuery(idea.pexels_search_term || '');
                      setPexelsSearchMode('video'); // primes default IF they choose Pexels later
                      // Only overwrite the headline if the user hasn't already
                      // written something meaningful. Preserves food-drive-style
                      // custom headlines when a user re-picks inspiration after
                      // swapping an asset.
                      if (!headline || headline.trim() === '') {
                        setHeadline(idea.title || '');
                        // Mark provenance so callAI doesn't LOCK this as a
                        // user-typed headline. The card text is a topic,
                        // not final wording — let the AI generate the real
                        // headline in the user's preferred language.
                        setHeadlineSource('inspiration');
                      }
                      setInspirationAnswer(idea.title || '');
                      setInspirationDismissed(true);
                    }}
                    className="eh-design-nodrag mt-auto text-[10px] font-medium text-[#00aef7] hover:text-[#0097d6] text-left"
                  >
                    {inspStrings.use_this_idea}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ──────── Generate New — seed-term modal ──────── */}
      {generateNewPromptOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 px-4"
          onClick={() => setGenerateNewPromptOpen(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">{inspStrings.modal_title}</h3>
              <button
                type="button"
                onClick={() => setGenerateNewPromptOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1 -mr-1"
                title={inspStrings.modal_cancel_title}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const hint = generateNewPromptText;
                setGenerateNewPromptOpen(false);
                handleGenerateNewInspiration(hint);
              }}
              className="px-5 py-4"
            >
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {inspStrings.modal_label}
              </label>
              <input
                type="text"
                autoFocus
                value={generateNewPromptText}
                onChange={(e) => setGenerateNewPromptText(e.target.value)}
                placeholder={generateNewPlaceholder}
                maxLength={120}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00aef7]/40 focus:border-[#00aef7] text-sm"
              />
              <p className="text-[11px] text-gray-400 mt-1.5 leading-snug">
                {inspStrings.modal_helper}
              </p>
              <div className="flex items-center justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setGenerateNewPromptOpen(false)}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                  {inspStrings.modal_cancel}
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 rounded-lg bg-[#00aef7] hover:bg-[#0098d8] text-white text-sm font-medium shadow-sm"
                >
                  {inspStrings.modal_submit}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ──────── MAIN WORKSPACE (two-column) ──────── */}
      <div className="flex-1 min-h-0 flex gap-3 relative">

      {/* ──────── LEFT PANE: Image Library ──────── */}
      <div className="w-[280px] shrink-0 bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
        {/* Header — search trigger */}
        <div className="px-2 py-2 border-b border-gray-200">
          <div className="eh-design-nodrag flex items-center gap-1 px-1.5 py-1.5 rounded-lg border border-gray-300 bg-gray-50">

            <span className="text-xs text-gray-400 truncate">Search eHandshake for</span>
            <div className="flex items-center gap-1.5 ml-auto shrink-0">
              {/* Videos comes first 2026-06-01 — Brett's call. Most social
                  platforms now favor video content for organic reach, so
                  we nudge users toward video as the default jumping-off
                  point. Hover tooltip on Images (item #3 in the queue)
                  will reinforce the same point in plain language. */}
              <button
                type="button"
                onClick={() => {
                  setPexelsInitialQuery(professionName || '');
                  setPexelsSearchMode('video');
                  setShowPexels(true);
                }}
                className="px-1.5 py-0.5 text-xs font-medium rounded bg-[#00aef7] text-white hover:bg-[#0097d6] transition"
              >
                Videos
              </button>
              <button
                type="button"
                onClick={() => {
                  setPexelsInitialQuery(professionName || '');
                  setPexelsSearchMode(null);
                  setShowPexels(true);
                }}
                className="px-1.5 py-0.5 text-xs font-medium rounded bg-[#00aef7] text-white hover:bg-[#0097d6] transition"
              >
                Images
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable thumbnails — 2 column grid, infinite scroll */}
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="flex-1 min-h-0 overflow-auto overscroll-contain p-1.5 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
        >
          {loading && !images.length ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00aef7]"></div>
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              {error}
            </div>
          ) : !images.length ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 text-xs text-gray-600">
              No images found for this category.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1.5">
              {images.map((item) => {
                const isActive = item.filename === selectedFilename;
                return (
                  <div key={item.filename} className="relative group">
                    <button
                      type="button"
                      onClick={() => setSelectedFilename(item.filename)}
                      className={`w-full rounded overflow-hidden border transition ${
                        isActive
                          ? 'border-[#00aef7] ring-2 ring-[#00aef7]/30'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${item.hidden ? 'opacity-35' : ''}`}
                      title={item.filename}
                    >
                      <div className="aspect-[4/3] bg-gray-100 relative">
                        <div className="absolute inset-0 flex items-center justify-center thumb-loader">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-[#00aef7]" />
                        </div>
                        <img
                          src={item.thumb_url}
                          alt=""
                          className="w-full h-full object-cover relative z-[1]"
                          loading="lazy"
                          onLoad={(e) => {
                            const loader = e.currentTarget.parentElement?.querySelector('.thumb-loader');
                            if (loader) loader.style.display = 'none';
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const loader = e.currentTarget.parentElement?.querySelector('.thumb-loader');
                            if (loader) loader.style.display = 'none';
                          }}
                        />
                        {item.asset_type === 'video' && (
                          <div className="absolute inset-0 flex items-center justify-center z-[2]">
                            <div className="w-7 h-7 rounded-full bg-black/50 flex items-center justify-center">
                              <svg className="w-3.5 h-3.5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                    </button>
                    {/* Hide/unhide X button — top right, visible on hover */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHideAsset(item.asset_id);
                      }}
                      className="eh-design-nodrag absolute top-0.5 right-0.5 z-[3] w-5 h-5 rounded-full bg-black/60 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      title={item.hidden ? 'Unhide image' : 'Hide image'}
                    >
                      {item.hidden ? (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9l6-6M15 9l6 6-6 6" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Loading more indicator */}
          {loadingMore && (
            <div className="flex items-center justify-center py-3">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#00aef7]"></div>
              <span className="ml-2 text-xs text-gray-400">Loading more...</span>
            </div>
          )}
        </div>
      </div>

      {/* ──────── RIGHT PANE: Preview + AI Tools ──────── */}
      <div className="flex-1 min-w-0 bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">

        {/* Main content area (NO scrolling here — internal panels handle their own scroll) */}
        <div className="flex-1 min-h-0 overflow-hidden px-3 pt-3 pb-2">

          {/* Outer flex row + chat panel ALWAYS render — the !selected
              empty state only swaps the canvas itself, not the whole right
              pane. Earlier attempt scoped the conditional too widely and
              hid the AI Assist sidebar when there was no asset picked. */}
          <div className="flex gap-3 min-h-0 h-full">
              {/* Preview column — video player or draggable image */}
              <div className="flex-[55] min-w-0 flex flex-col">
                <div className="relative">
                {!selected ? (
                  // Blank-canvas empty state. Shows on Design Center entry
                  // (no auto-loaded asset — see fetchPage) and any time the
                  // gallery is empty or the previous selection got removed.
                  // Matches the 4:3 canvas aspect so the layout doesn't
                  // jump when the user picks an asset. White-on-black is
                  // Brett's spec — high-contrast film-slate look.
                  <div className="w-full aspect-[4/3] rounded-lg flex items-center justify-center bg-black text-white text-lg font-medium px-6 text-center select-none">
                    Choose or upload a video or image
                  </div>
                ) : selected.asset_type === 'video' ? (
                  <div ref={videoContainerRef} className="relative w-full aspect-[4/3] rounded-lg overflow-hidden border border-gray-200 bg-black">
                    <video
                      ref={inlineVideoRef}
                      key={selected.filename}
                      src={selected.url}
                      controls
                      controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                      disablePictureInPicture
                      disableRemotePlayback
                      className="w-full h-full object-contain"
                      preload="metadata"
                      onFullScreenChange={(e) => e.preventDefault()}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                    <HeadlineOverlay
                      containerRef={videoContainerRef}
                      headline={headline}
                      onHeadlineChange={setHeadlineFromUser}
                      headlinePos={headlinePos}
                      onHeadlinePosChange={setHeadlinePos}
                      headlineStyle={headlineStyle}
                      onHeadlineStyleChange={setHeadlineStyle}
                    />
                  </div>
                ) : (
                  <DraggablePreview
                    key={selected.filename}
                    src={selected.url}
                    alt=""
                    position={selectedPosition}
                    zoom={selectedZoom}
                    onPositionChange={handlePositionChange}
                    onZoomChange={handleZoomChange}
                    headline={headline}
                    onHeadlineChange={setHeadlineFromUser}
                    headlinePos={headlinePos}
                    onHeadlinePosChange={setHeadlinePos}
                    headlineStyle={headlineStyle}
                    onHeadlineStyleChange={setHeadlineStyle}
                  />
                )}
                </div>{/* end relative wrapper for lightbulb */}

                {/* ─── Headline Styling Toolbar (images only) ─── */}
                {headline && (
                  <div className="shrink-0 mt-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 flex flex-col gap-1.5">
                    {/* Row 1: Font size + Reset position/zoom */}
                    <div className="flex items-center gap-2">
                      {/* Font size controls */}
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-500 w-5">Aa</span>
                        <button
                          type="button"
                          onClick={() => setHeadlineStyle((s) => ({ ...s, fontSize: Math.max(10, s.fontSize - 2) }))}
                          className="eh-design-nodrag w-5 h-5 rounded border border-gray-300 bg-white text-[10px] text-gray-600 hover:bg-gray-100 flex items-center justify-center leading-none"
                          title="Decrease font size"
                        >
                          −
                        </button>
                        <span className="text-[10px] text-gray-600 w-6 text-center tabular-nums">{headlineStyle.fontSize}</span>
                        <button
                          type="button"
                          onClick={() => setHeadlineStyle((s) => ({ ...s, fontSize: Math.min(48, s.fontSize + 2) }))}
                          className="eh-design-nodrag w-5 h-5 rounded border border-gray-300 bg-white text-[10px] text-gray-600 hover:bg-gray-100 flex items-center justify-center leading-none"
                          title="Increase font size"
                        >
                          +
                        </button>
                      </div>

                      <div className="w-px h-4 bg-gray-300" />

                      {/* Background opacity */}
                      <div className="flex items-center gap-1 flex-1 min-w-0">
                        <span className="text-[10px] text-gray-500 shrink-0">BG</span>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={Math.round(headlineStyle.bgOpacity * 100)}
                          onChange={(e) => setHeadlineStyle((s) => ({ ...s, bgOpacity: Number(e.target.value) / 100 }))}
                          className="eh-design-nodrag flex-1 min-w-0 h-1 accent-[#00aef7]"
                          title={`Background opacity: ${Math.round(headlineStyle.bgOpacity * 100)}%`}
                        />
                        <span className="text-[10px] text-gray-500 w-7 text-right tabular-nums">{Math.round(headlineStyle.bgOpacity * 100)}%</span>
                      </div>

                      <div className="w-px h-4 bg-gray-300" />

                      {/* Reset position/zoom button */}
                      {hasEdits && (
                        <button
                          type="button"
                          onClick={handleResetPosition}
                          className="eh-design-nodrag text-[10px] text-gray-500 hover:text-[#00aef7] shrink-0"
                          title="Reset image position & zoom"
                        >
                          Reset
                        </button>
                      )}
                    </div>

                    {/* Row 2: Color palette */}
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-gray-500 w-5 shrink-0">Color</span>
                      <div className="flex items-center gap-0.5 flex-wrap">
                        {HEADLINE_COLORS.map((c) => (
                          <button
                            key={c.hex}
                            type="button"
                            onClick={() => setHeadlineStyle((s) => ({ ...s, color: c.hex }))}
                            className={`eh-design-nodrag w-4 h-4 rounded-full border-2 transition-transform ${
                              headlineStyle.color === c.hex
                                ? 'border-[#00aef7] scale-125'
                                : 'border-gray-300 hover:scale-110'
                            }`}
                            style={{ backgroundColor: c.hex }}
                            title={c.name}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── Upload Button + Disclaimer ─── */}
                <div className="shrink-0 mt-1.5">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/x-msvideo,video/webm"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                    className={`eh-design-nodrag w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition ${
                      uploading
                        ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-wait'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-[#00aef7] hover:text-[#00aef7]'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-[#00aef7]" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                        </svg>
                        Upload Video or Image
                      </>
                    )}
                  </button>
                  <p className="text-[9px] text-gray-400 text-Left mt-1 leading-tight">
                    By uploading, you confirm you own or have the right to use this content. You are responsible for ensuring it does not infringe on any third-party rights.
                  </p>
                </div>

              </div>
              {/* AI Assist (separate component) */}
              <div className="flex-[45] min-w-0 flex flex-col gap-2 min-h-0">
                <DesignCenterAIAssist
                  key={aiResetKey}
                  selected={selected}
                  activeTabLabel={activeTabLabel}
                  sectionKey={subcategoryKey}
                  // Pass the user's current headline so the AI can preserve
                  // it instead of replacing it with its own generation —
                  // BUT only when the user actually typed it (provenance
                  // 'user'). Headlines auto-set from inspiration cards
                  // pass headlineSource='inspiration' and AIAssist will
                  // NOT lock them, letting the AI generate a fresh
                  // headline in the user's preferred language.
                  existingHeadline={headline}
                  headlineIsUserTyped={headlineSource === 'user'}
                  onCaptionsGenerated={handleCaptionsGenerated}
                  onBriefComplete={(b) => setAiBrief(b)}
                  inspirationAnswer={inspirationAnswer}
                  inspirationDismissed={inspirationDismissed}
                  inspirationLoading={inspirationLoading}
                  inspirationIdeas={inspirationIdeas}
                  onReopenInspiration={() => setInspirationDismissed(false)}
                />
              </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-3 py-2 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              if (!headline || !captions) return;
              setPreviewKey((k) => k + 1);
              setShowPreview(true);
            }}
            disabled={!headline || !captions}
            className={`eh-design-nodrag px-4 py-1 rounded-lg text-white text-sm ${
              headline && captions
                ? 'bg-[#00aef7] hover:bg-[#0097d6]'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Preview by Social Platform
          </button>
        </div>

        {/* Publish Preview Modal */}
        {showPreview && selected && (
          <PublishPreviewModal
            key={previewKey}
            imageSrc={selected.url}
            imageAssetId={selected.asset_id}
            assetType={selected.asset_type}
            existingTiktokVideoAssetId={tiktokVideoAssetId}
            position={selectedPosition}
            zoom={selectedZoom}
            headline={headline}
            headlinePos={headlinePos}
            headlineStyle={headlineStyle}
            captions={captions}
            businessInfo={businessInfo}
            professionName={professionName}
            onClose={(generatedTiktokAssetId) => {
              if (generatedTiktokAssetId) {
                setTiktokVideoAssetId(generatedTiktokAssetId);
                tiktokVideoAssetIdRef.current = generatedTiktokAssetId;
              }
              setShowPreview(false);
            }}
            onPublish={(tiktokAssetId, editedCaptions, editedOverlays) => {
              const val = tiktokAssetId || null;
              setTiktokVideoAssetId(val);
              tiktokVideoAssetIdRef.current = val;
              // Commit the per-platform edits from the preview modal
              if (editedCaptions) setCaptions(editedCaptions);
              if (editedOverlays && Object.keys(editedOverlays).length > 0) {
                setPlatformOverlays(editedOverlays);
              }
              setShowPreview(false);
              setShowLandingDecision(true);
            }}
          />
        )}
      </div>
      </div>{/* end MAIN WORKSPACE two-column */}

      {/* Expanded Video Overlay */}
      {videoExpanded && selected?.asset_type === 'video' && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60"
          onClick={() => {
            const ev = expandedVideoRef.current;
            if (ev) {
              videoTimeRef.current = ev.currentTime;
              videoWasPlayingRef.current = !ev.paused;
              ev.pause();
            }
            setVideoExpanded(false);
            // Sync back to inline
            setTimeout(() => {
              const iv = inlineVideoRef.current;
              if (iv) {
                iv.currentTime = videoTimeRef.current;
                if (videoWasPlayingRef.current) iv.play();
              }
            }, 50);
          }}
        >
          <div
            className="relative bg-black rounded-xl overflow-hidden shadow-2xl"
            style={{ width: '65vw', maxWidth: '1100px', maxHeight: '80vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => {
                const ev = expandedVideoRef.current;
                if (ev) {
                  videoTimeRef.current = ev.currentTime;
                  videoWasPlayingRef.current = !ev.paused;
                  ev.pause();
                }
                setVideoExpanded(false);
                setTimeout(() => {
                  const iv = inlineVideoRef.current;
                  if (iv) {
                    iv.currentTime = videoTimeRef.current;
                    if (videoWasPlayingRef.current) iv.play();
                  }
                }, 50);
              }}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition"
              title="Close"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
            <video
              ref={expandedVideoRef}
              src={selected.url}
              controls
              controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
              disablePictureInPicture
              disableRemotePlayback
              className="w-full"
              style={{ maxHeight: '80vh' }}
              onLoadedMetadata={(e) => {
                e.currentTarget.currentTime = videoTimeRef.current;
                if (videoWasPlayingRef.current) e.currentTarget.play();
              }}
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>
        </div>
      )}

      {/* Pexels Search Modal */}
      {showPexels && (
        <PexelsSearchModal
          subcategory={subcategoryKey}
          initialQuery={pexelsInitialQuery}
          professionId={professionId}
          maxSelect={10}
          searchMode={pexelsSearchMode}
          onImported={(importedData) => {
            // Optimistic prepend: build the full asset shape locally and
            // splice it into `images`, then set selection. This avoids a
            // race where fetchPage refreshes from /profession-images before
            // the new asset has settled into page 1's sort order — the
            // server uses a random MD5 hash to break ties among
            // user_selected=true rows, so a freshly imported asset can
            // sometimes fall onto page 2. When that happened, fetchPage's
            // selection-preservation logic fell back to images[0] and the
            // canvas showed an unrelated asset (most visible bug: pick a
            // video, the editor never showed it).
            //
            // We still kick off a server refresh in the background so the
            // strip reflects any other newly available rows, but we don't
            // depend on it to surface the just-imported asset.
            if (!Array.isArray(importedData) || importedData.length === 0) return;

            // Pre-1 backend versions of /pexels/(videos/)import don't set
            // asset_type. Fall back to pexelsSearchMode in that case.
            const fallbackAssetType = pexelsSearchMode === 'video' ? 'video' : 'image';

            const newAssets = importedData.map((item) => ({
              asset_id: item.asset_id,
              filename: item.filename,
              url: item.url,
              thumb_url: item.thumb_url || item.url,
              alt_text: '',
              asset_type: item.asset_type || fallbackAssetType,
              user_selected: true,
              source: 'pexels',
              hidden: false,
            }));

            setImages((prev) => {
              const newIds = new Set(newAssets.map((a) => a.asset_id));
              // Dedup against existing rows so a re-import doesn't double-up.
              const remaining = prev.filter((img) => !newIds.has(img.asset_id));
              return [...newAssets, ...remaining];
            });

            // Selection lands on the first imported asset. The canvas reads
            // `selected = images.find(filename === selectedFilename)`, which
            // now resolves immediately because we just prepended.
            setSelectedFilename(newAssets[0].filename);

            // Intentionally NOT calling fetchPage here. It would re-fetch
            // page 1 of /profession-images and then run its own selection-
            // preservation logic against the *server's* result list. If the
            // just-imported asset hashes onto page 2 (which is random across
            // the user_selected=true tier), fetchPage's fallback clobbers
            // our selection. The optimistic insert above is enough — the
            // user sees their pick immediately, and the strip's existing
            // images stay valid. Server state catches up on next category
            // switch or infinite-scroll page.
          }}
          onClose={() => { setShowPexels(false); setPexelsInitialQuery(''); setPexelsSearchMode(null); }}
        />
      )}

      {/* ─── Moderation Warning ──────────────────────────────────────────── */}
      {moderationAlert && (
        <div className="fixed bottom-4 right-4 z-[9999] w-[420px]">
          <ModerationWarning moderation={moderationAlert} onDismiss={() => setModerationAlert(null)} />
        </div>
      )}

      {/* ─── Landing Page Decision Modal ─────────────────────────────────── */}
      {showLandingDecision && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-[520px] max-w-[95vw] overflow-hidden">
            {/* Header */}
            <div className="px-6 pt-6 pb-2">
              <h2 className="text-xl font-bold text-gray-900">Where should visitors go?</h2>
              <p className="text-sm text-gray-500 mt-1">When someone clicks your social media post, where do you want them to land?</p>
            </div>

            {/* Options */}
            <div className="px-6 py-4 space-y-3">
              {/* Option A: My Website */}
              <div className="border-2 border-gray-200 rounded-xl p-4 hover:border-blue-400 transition-colors cursor-pointer group"
                   onClick={() => { /* expand URL input */ }}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-50">
                    <svg className="w-5 h-5 text-gray-500 group-hover:text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">Link to My Landing Page</h3>
                    <p className="text-xs text-gray-500 mt-0.5">You can link to any of your public-facing pages. For example, if you posted a Pin on Pinterest or a video to Youtube, you can link this post to that page. Or if you have Blog articles on your website, you can do a post for each one.</p>
                    <div className="mt-3">
                      <input
                        type="text"
                        value={externalUrl}
                        onChange={(e) => setExternalUrl(e.target.value)}
                        placeholder="https://yourwebsite.com/page"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                        onClick={(e) => e.stopPropagation()}
                      />
                      {externalUrl.trim() && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            setModerationAlert(null);
                            // Open Sharing Center with throbber
                            openSharingCenter(true);
                            bringToFront('sharingCenter');
                            try {
                              const res = await fetch(`${API_URL}/api/designcenter/posts`, {
                                method: 'POST',
                                credentials: 'include',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  headline,
                                  captions,
                                  imageAssetId: selected?.asset_id || null,
                                  headlineStyle: { ...headlineStyle, posX: headlinePos.x, posY: headlinePos.y },
                                  platformOverlays: platformOverlays || null,
                                  categoryKey: subcategoryKey,
                                  externalUrl: externalUrl.trim(),
                                  tiktokVideoAssetId: tiktokVideoAssetIdRef.current || null,
                                  // Persist the brief so the auto-landing-page generator
                                  // can honor the user's language/tone (e.g., conversational Thai).
                                  brief: aiBrief || null,
                                }),
                              });
                              const data = await res.json();
                              if (data.moderation) {
                                setSharingCenterPending(false);
                                setSharingCenterModerationMsg('Cannot publish this to the Sharing Center until the content is approved.');
                                setModerationAlert({
                                  moderationId: data.moderationId,
                                  categories: data.categories,
                                  severity: data.severity,
                                  reason: data.reason,
                                });
                                return;
                              }
                              if (data.limitReached) {
                                setSharingCenterPending(false);
                                setSharingCenterModerationMsg(`You've reached your limit of ${data.limit} active creation${data.limit === 1 ? '' : 's'} for this month. Delete an existing creation or upgrade your plan to publish more.`);
                                return;
                              }
                            } catch (err) {
                              console.error('[DesignCenter] save post error:', err);
                            }
                            setSharingCenterPending(false);
                            // Direct refresh — pending toggle alone has been
                            // unreliable when fetchPosts identity changes mid-flight.
                            document.dispatchEvent(new CustomEvent('sharing-center-refresh'));
                            resetWorkspace();
                          }}
                          className="mt-2 px-4 py-1.5 bg-[#00aef7] text-white text-sm font-medium rounded-lg hover:bg-[#0097d6]"
                        >
                          Use This Link
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Option B: Build Landing Page */}
              <div
                className="border-2 border-gray-200 rounded-xl p-4 hover:border-purple-400 transition-colors cursor-pointer group"
                onClick={async () => {
                  if (generatingLanding) return;
                  setGeneratingLanding(true);
                  setError && setError('');
                  try {
                    const res = await fetch(`${API_URL}/api/designcenter/landing-pages`, {
                      method: 'POST',
                      credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        headline: headline || '',
                        category: subcategoryKey || 'general',
                        brief: aiBrief || { goal: headline || 'social media post' },
                        heroImageId: selected?.asset_id || null,
                      }),
                    });
                    const data = await res.json();
                    if (data.ok) {
                      setLandingPageId(data.landing_page_id);
                      setLandingPageData({
                        landing_page_id: data.landing_page_id,
                        slug: data.slug,
                        layout: 'bold-hero',
                        accent_color: '#2563EB',
                        hero_headline: data.hero_headline,
                        hero_subheadline: data.hero_subheadline,
                        sections: data.sections,
                        cta_type: 'contact_us',
                        cta_text: data.cta_text,
                        cta_url: '',
                        hero_image_id: selected?.asset_id || null,
                        hero_image_url: selected?.url ? `${API_URL}${selected.url}` : null,
                        hero_asset_type: selected?.asset_type || 'image',
                      });
                      setShowLandingDecision(false);
                      setShowLandingBuilder(true);
                    } else {
                      console.error('[DesignCenter] Landing page generation failed:', data.error);
                    }
                  } catch (err) {
                    console.error('[DesignCenter] Landing page generation error:', err);
                  }
                  setGeneratingLanding(false);
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-100">
                    {generatingLanding ? (
                      <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">Build a Landing Page</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {generatingLanding
                        ? 'AI is generating your landing page content...'
                        : 'AI will create a professional landing page hosted on eHandshake. Edit it, add images, publish instantly.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setShowLandingDecision(false)}
                className="px-4 py-1.5 text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Landing Page Builder (full-screen) ──────────────────────────── */}
      {showLandingBuilder && landingPageId && (
        <LandingPageBuilder
          landingPageId={landingPageId}
          initialData={landingPageData}
          onClose={() => setShowLandingBuilder(false)}
          onPublished={async (lp) => {
            // Save the social post to the blog, linked to the landing page
            setModerationAlert(null);
            // Open Sharing Center with throbber
            openSharingCenter(true);
            bringToFront('sharingCenter');
            try {
              const res = await fetch(`${API_URL}/api/designcenter/posts`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  headline,
                  captions,
                  imageAssetId: selected?.asset_id || null,
                  headlineStyle: { ...headlineStyle, posX: headlinePos.x, posY: headlinePos.y },
                  platformOverlays: platformOverlays || null,
                  categoryKey: subcategoryKey,
                  landingPageId: lp?.landing_page_id || landingPageId,
                  tiktokVideoAssetId: tiktokVideoAssetIdRef.current || null,
                  // Persist the brief alongside the post for parity with the
                  // external-URL publish path. Manual landing pages already
                  // persist their own brief_json on the landing page row.
                  brief: aiBrief || null,
                }),
              });
              const data = await res.json();
              if (data.moderation) {
                setSharingCenterPending(false);
                setSharingCenterModerationMsg('Cannot publish this to the Sharing Center until the content is approved.');
                setModerationAlert({
                  moderationId: data.moderationId,
                  categories: data.categories,
                  severity: data.severity,
                  reason: data.reason,
                });
                return;
              }
              if (data.limitReached) {
                setSharingCenterPending(false);
                setSharingCenterModerationMsg(`You've reached your limit of ${data.limit} active creation${data.limit === 1 ? '' : 's'}. Delete an existing creation or upgrade your plan to publish more.`);
                return;
              }
            } catch (err) {
              console.error('[DesignCenter] save post error:', err);
            }
            setSharingCenterPending(false);
            // Direct refresh — pending toggle alone has been unreliable
            // when fetchPosts identity changes mid-flight.
            document.dispatchEvent(new CustomEvent('sharing-center-refresh'));
            resetWorkspace();
          }}
        />
      )}
    </div>
  );
}
